<script>
/**
 * @component AIAuditModal
 * @description Modal de análisis de caso de auditoría con IA.
 *              Genera resumen, análisis clínico, recomendación y dictamen sugerido.
 * @prop {boolean} visible - Controla visibilidad del modal
 * @prop {string} auditCaseId - ID del caso de auditoría
 * @prop {Object} auditCase - Datos del caso para mostrar en el header
 * @emits close - Al cerrar el modal
 * @emits apply-dictum - Al aplicar el dictamen sugerido, emite el texto
 */
import api from "@/services/api";

const RECOMMENDATION_LABELS = {
  approve: { label: "Aprobar", color: "#16a34a", bg: "#dcfce7" },
  reject: { label: "Rechazar", color: "#dc2626", bg: "#fee2e2" },
  review: { label: "Requiere revisión", color: "#d97706", bg: "#fef3c7" },
};

export default {
  name: "AIAuditModal",

  props: {
    visible: { type: Boolean, default: false },
    auditCaseId: { type: String, default: "" },
    auditCase: { type: Object, default: null },
  },

  data() {
    return {
      loading: false,
      result: null,
      error: null,
    };
  },

  computed: {
    /**
     * @description Metadata de la recomendación actual
     * @returns {Object}
     */
    recommendationMeta() {
      if (!this.result?.recomendacion) return null;
      return RECOMMENDATION_LABELS[this.result.recomendacion] || null;
    },
  },

  watch: {
    visible(val) {
      if (val && !this.result) this.analyze();
    },
  },

  methods: {
    /**
     * @description Llama al endpoint de análisis de auditoría
     */
    async analyze() {
      try {
        this.loading = true;
        this.error = null;
        const data = await api
          .post("/ai/audit-analysis", { auditCaseId: this.auditCaseId })
          .then((r) => r.data);
        this.result = data;
      } catch (e) {
        this.error = e?.response?.data?.message || "Error al analizar el caso.";
      } finally {
        this.loading = false;
      }
    },

    /**
     * @description Aplica el dictamen sugerido al formulario
     */
    applyDictum() {
      if (this.result?.dictamen_sugerido) {
        this.$emit("apply-dictum", this.result.dictamen_sugerido);
        this.$message.success("Dictamen aplicado al formulario");
      }
    },

    /**
     * @description Cierra el modal y resetea el estado
     */
    close() {
      this.result = null;
      this.error = null;
      this.$emit("close");
    },
  },
};
</script>

<template lang="pug">
el-dialog(
  :visible="visible"
  width="680px"
  :before-close="close"
  custom-class="ai-audit-dialog"
)
  template(slot="title")
    .ai-audit-dialog__header
      .ai-audit-dialog__header-icon
        i.el-icon-magic-stick
      .ai-audit-dialog__header-text
        h3.ai-audit-dialog__title Análisis IA — Auth {{ auditCase && auditCase.authCaseNumber }}
        p.ai-audit-dialog__subtitle Análisis clínico del caso con sugerencia de dictamen

  .ai-audit-dialog__body
    //- Loading
    .ai-audit-dialog__loading(v-if="loading")
      i.el-icon-loading
      span Analizando el caso...

    //- Error
    .ai-audit-dialog__error(v-else-if="error")
      i.el-icon-warning-outline
      span {{ error }}
      el-button(type="text" size="mini" @click="analyze") Reintentar

    //- Resultado
    template(v-else-if="result")
      //- Recomendación destacada
      .ai-audit-recommendation(
        v-if="recommendationMeta"
        :style="{ background: recommendationMeta.bg, borderColor: recommendationMeta.color }"
      )
        span.ai-audit-recommendation__label(:style="{ color: recommendationMeta.color }") Recomendación:
        span.ai-audit-recommendation__value(:style="{ color: recommendationMeta.color }") {{ recommendationMeta.label }}

      //- Secciones
      .ai-audit-section
        .ai-audit-section__title Qué se solicita
        p.ai-audit-section__body {{ result.resumen }}

      .ai-audit-section
        .ai-audit-section__title Análisis clínico
        p.ai-audit-section__body {{ result.analisis_clinico }}

      .ai-audit-section(v-if="result.historial_relevante")
        .ai-audit-section__title Historial relevante
        p.ai-audit-section__body {{ result.historial_relevante }}

      .ai-audit-section
        .ai-audit-section__title Justificación
        p.ai-audit-section__body {{ result.justificacion }}

      .ai-audit-section.ai-audit-section--dictum
        .ai-audit-section__header
          span.ai-audit-section__title Dictamen sugerido
          el-button(
            type="primary"
            size="mini"
            @click="applyDictum"
          ) Usar este dictamen
        pre.ai-audit-section__dictum {{ result.dictamen_sugerido }}

  template(slot="footer")
    .ai-audit-dialog__footer
      el-button(size="small" @click="analyze" :loading="loading") Re-analizar
      el-button(size="small" @click="close") Cerrar
</template>

<style lang="scss" scoped>
.ai-audit-dialog {
  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__header-icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: #e6f9f9;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    i {
      font-size: 18px;
      color: #009b7d;
    }
  }

  &__title {
    font-size: 15px;
    font-weight: 700;
    color: $navy;
    margin: 0;
  }

  &__subtitle {
    font-size: 12px;
    color: $text-secondary-new;
    margin: 0;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 150px;
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 60px 20px;
    color: $text-secondary-new;
    font-size: 14px;
    i {
      font-size: 28px;
      color: $teal;
    }
  }

  &__error {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 40px 20px;
    color: #dc2626;
    font-size: 13px;
    text-align: center;
    i {
      font-size: 24px;
    }
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
}

.ai-audit-recommendation {
  border: 1.5px solid;
  border-radius: 8px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 8px;

  &__label {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  &__value {
    font-size: 14px;
    font-weight: 700;
  }
}

.ai-audit-section {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;

  &__title {
    font-size: 11px;
    font-weight: 700;
    color: $navy;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    padding: 7px 14px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    display: block;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 7px 14px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }

  &__body {
    padding: 10px 14px;
    font-size: 13.5px;
    color: #334155;
    line-height: 1.6;
    margin: 0;
  }

  &__dictum {
    padding: 10px 14px;
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 13px;
    color: #334155;
    line-height: 1.6;
    white-space: pre-wrap;
    word-wrap: break-word;
    margin: 0;
  }

  &--dictum {
    border-color: #002d5e;
  }
}
</style>
